import { isEqual, keyBy } from "lodash";
import { SearchIcon, XIcon } from "lucide-react";
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useState,
} from "react";
import { useDebounce } from "use-debounce";

import {
  AsyncJobStatusEnum,
  AsyncStreamsStatus,
  ConnectionsQuery,
  SchemasQuery,
  Stream,
  useCancelStreamsAsyncJobMutation,
  useGetStreamsAsyncQuery,
  useGetStreamsQuery,
  useSchemasQuery,
} from "@/apollo/types";
import { Button, UtilityButton } from "@/components/elements/Button";
import { FormatRelativeTime } from "@/components/elements/FormatRelativeTime";
import LoadingSpinner from "@/components/elements/LoadingSpinner";
import {
  Input,
  InputAddonLeft,
  InputGroup,
} from "@/components/primitives/input";
import {
  CdcUnavailableNotice,
  ConnectionCDCFeaturesProvider,
} from "@/entities/connections";
import {
  SourceStreamsAction,
  SourceStreamsState,
  sourceStreamsReducer,
} from "@/features/elt/reducers/sourceStreamsReducer";
import cn from "@/helpers/classNames";
import { useLatestValueRef } from "@/hooks/useLatestValueRef";
import { useIntegration } from "@/integrations";

import StreamItem from "./StreamItem";

type LazySchema = SchemasQuery["getAllAvroSchemas"]["schemas"][number];

const SOURCE_SETTINGS_DEBOUNCE_MS = 500;

export enum Mode {
  CREATE = "create",
  EDIT = "edit",
}

export const SourceStreamsStateContext = createContext<
  [SourceStreamsState, React.Dispatch<SourceStreamsAction>] | null
>(null);

export function useSourceStreamsState() {
  const context = useContext(SourceStreamsStateContext);
  if (context === null) {
    throw new Error(
      "useSourceStreamsState must be used within a SourceStreamStateProvider",
    );
  }
  return context;
}

export default function TableConfig(props: {
  sourceId: string;
  sourceSettings: Record<string, any>;
  connection: ConnectionsQuery["connections"][0];
  mode: Mode;
  initialValues?: SourceStreamsState;
  onChange: (sourceStreams: SourceStreamsState) => void;
  onError?: (error: Error) => void;
  onCancel?: () => void;
  requiredStreams?: string[];
  size?: "small" | "large";
  eltSyncId?: string;
  syncInterval?: string | null;
  /** Per-table overrides of that cadence, by stream name. */
  streamSyncIntervals?: Record<string, string | null>;
}) {
  const [sourceStreams, dispatch] = useReducer(
    sourceStreamsReducer,
    props.initialValues ?? {},
  );

  const onChangeRef = useLatestValueRef(props.onChange);
  useEffect(() => {
    onChangeRef.current(sourceStreams);
  }, [sourceStreams, onChangeRef]);

  const sourceIntegration = useIntegration(props.connection.integrationId);

  const [search, setSearch] = useState("");
  const [asyncStreamsStatus, setStreamsStatus] = useState<AsyncStreamsStatus>({
    status: AsyncJobStatusEnum.Processing,
  });

  const [sourceSettings] = useDebounce(
    props.sourceSettings,
    SOURCE_SETTINGS_DEBOUNCE_MS,
    { equalityFn: isEqual },
  );

  const usesAsyncStreams = sourceIntegration?.config?.usesAsyncStreams === true;

  const getStreamsQuery = useGetStreamsQuery({
    skip: !props.connection?.id || usesAsyncStreams,
    variables: {
      connectionId: props.connection?.id || "",
      sourceSettings,
      eltSyncId: props.eltSyncId,
    },
    onError: (error) => props.onError?.(error),
  });
  const fetchedStreams = getStreamsQuery.data?.getStreams;
  // Keeps the current list on screen while a settings change is refetching.
  const syncStreams =
    fetchedStreams ?? getStreamsQuery.previousData?.getStreams;

  const modeRef = useLatestValueRef(props.mode);
  useEffect(() => {
    if (!fetchedStreams) return;
    dispatch({
      type: "reset_streams",
      payload: {
        availableStreamNames: fetchedStreams.map((s) => s.name),
      },
    });
    if (fetchedStreams.length === 1) {
      dispatch({
        type: "set_source_stream",
        payload: {
          checked: true,
          stream: fetchedStreams[0],
        },
      });
    } else if (modeRef.current === Mode.CREATE) {
      for (const stream of fetchedStreams.filter((s) => s.preselect)) {
        dispatch({
          type: "set_source_stream",
          payload: {
            checked: true,
            stream,
          },
        });
      }
    }
  }, [fetchedStreams, modeRef]);

  const streamsStatus = useMemo<AsyncStreamsStatus>(() => {
    if (usesAsyncStreams) return asyncStreamsStatus;
    return syncStreams
      ? { status: AsyncJobStatusEnum.Done, streams: syncStreams }
      : { status: AsyncJobStatusEnum.Processing };
  }, [usesAsyncStreams, asyncStreamsStatus, syncStreams]);

  const streams = useMemo(
    () => streamsStatus.streams || [],
    [streamsStatus.streams],
  );

  const { stopPolling } = useGetStreamsAsyncQuery({
    variables: {
      connectionId: props.connection?.id || "",
      sourceSettings,
    },
    skip: !props.connection?.id || !usesAsyncStreams,
    fetchPolicy: "cache-and-network",
    notifyOnNetworkStatusChange: true,
    pollInterval: 1000,
    onCompleted: (data) => {
      setStreamsStatus(data.getStreamsAsync);
      switch (data.getStreamsAsync.status) {
        case AsyncJobStatusEnum.Done:
          if (data.getStreamsAsync.streams) {
            dispatch({
              type: "reset_streams",
              payload: {
                availableStreamNames: data.getStreamsAsync.streams.map(
                  (s) => s.name,
                ),
              },
            });
          }
          if (data.getStreamsAsync.streams?.length === 1) {
            dispatch({
              type: "set_source_stream",
              payload: {
                checked: true,
                stream: data.getStreamsAsync.streams[0],
              },
            });
          }
          stopPolling();
          return;
        case AsyncJobStatusEnum.Error:
          props.onError?.(
            new Error(data.getStreamsAsync.errorMessage ?? "Unknown error"),
          );
          stopPolling();
          return;
      }
    },
    onError: (error) => {
      props.onError?.(error);
      stopPolling();
    },
  });

  const [cancelStreamsAsyncJob, { loading: loadingCancel }] =
    useCancelStreamsAsyncJobMutation();
  const handleCancel = useCallback(async () => {
    if (
      !props.connection?.id ||
      streamsStatus.status !== AsyncJobStatusEnum.Processing ||
      loadingCancel
    ) {
      return;
    }
    await cancelStreamsAsyncJob({
      variables: {
        connectionId: props.connection.id,
      },
      onCompleted: () => {
        props.onCancel?.();
      },
    });
  }, [props, streamsStatus.status, loadingCancel, cancelStreamsAsyncJob]);

  const substreamCount = useMemo(
    () => streams.reduce((acc, stream) => acc + stream.substreams.length, 0),
    [streams],
  );

  const allTablesCount = streams.length + substreamCount;

  const orderedStreams = useMemo(() => {
    return [...streams].sort((a, b) => {
      const aRequired = props.requiredStreams?.includes(a.name) ?? false;
      const bRequired = props.requiredStreams?.includes(b.name) ?? false;
      if (aRequired && !bRequired) return -1;
      if (bRequired && !aRequired) return 1;
      if (a.name < b.name) return -1;
      if (a.name === b.name) return 0;
      return 1;
    });
  }, [streams, props.requiredStreams]);

  const filteredStreams = useMemo<any[]>(
    () =>
      orderedStreams.filter(
        (stream) =>
          stream.name.toLowerCase().includes(search.toLowerCase()) ||
          stream.substreams.some((s) =>
            s.destinationTableName.toLowerCase().includes(search.toLowerCase()),
          ),
      ),
    [search, orderedStreams],
  );

  const filteredSubstreamCount = useMemo(
    () =>
      filteredStreams.reduce(
        (acc, stream) => acc + stream.substreams.length,
        0,
      ),
    [filteredStreams],
  );

  const allFilteredTablesCount =
    filteredStreams.length + filteredSubstreamCount;

  const selectableStreams = useMemo(
    () => streams.filter((s) => s.selectable),
    [streams],
  );

  /**
   * A stream can be checked (or required) while non-selectable, e.g. from
   * `initialValues`/existing sync state predating the source no longer
   * offering it. Those still need a schema so `StreamItem` isn't stuck
   * showing its loading spinner forever.
   */
  const streamNamesForBulkSchemaFetch = useMemo(() => {
    const names = new Set(selectableStreams.map((s) => s.name));
    Object.entries(sourceStreams).forEach(([name, stream]) => {
      if (stream) names.add(name);
    });
    props.requiredStreams?.forEach((name) => names.add(name));
    return Array.from(names);
  }, [selectableStreams, sourceStreams, props.requiredStreams]);

  const schemasQuery = useSchemasQuery({
    variables: {
      sourceStreamNames: streamNamesForBulkSchemaFetch,
      sourceConnectionId: props.sourceId,
      connectionSettings: sourceSettings,
    },
    skip:
      !streamNamesForBulkSchemaFetch.length ||
      sourceIntegration?.config?.usesLazySchemas === true,
    onError: (error) => props.onError?.(error),
  });

  const usesLazySchemas = sourceIntegration?.config?.usesLazySchemas === true;

  /**
   * For integrations that use lazy schemas the bulk `schemasQuery` above is
   * skipped. We accumulate the schemas we've loaded so far and only query for
   * checked streams we haven't fetched yet. On the first load every checked
   * stream is missing, so they get fetched together in a single batched query;
   * afterwards each newly checked stream is fetched on its own.
   */
  const [lazySchemas, setLazySchemas] = useState<Record<string, LazySchema>>(
    {},
  );

  /**
   * Reset the accumulated schemas when the source connection changes, otherwise
   * schemas fetched for a previous source would be incorrectly reused (and
   * `streamNamesToFetch` would skip refetching them since they're already
   * present in the cache).
   */
  const sourceSettingsKey = useMemo(
    () => JSON.stringify(sourceSettings),
    [sourceSettings],
  );
  useEffect(() => {
    setLazySchemas({});
  }, [props.sourceId, sourceSettingsKey]);

  const streamNamesToFetch = useMemo(
    () =>
      Object.entries(sourceStreams)
        .filter(([name, stream]) => Boolean(stream) && !lazySchemas[name])
        .map(([name]) => name)
        .sort(),
    [sourceStreams, lazySchemas],
  );

  useSchemasQuery({
    variables: {
      sourceStreamNames: streamNamesToFetch,
      sourceConnectionId: props.sourceId,
      connectionSettings: sourceSettings,
    },
    skip: !usesLazySchemas || streamNamesToFetch.length === 0,
    onError: (error) => props.onError?.(error),
    onCompleted: (data) => {
      setLazySchemas((prev) => ({
        ...prev,
        ...keyBy(data.getAllAvroSchemas.schemas, "sourceStreamName"),
      }));
    },
  });

  const mappedSchemas = streams.length
    ? usesLazySchemas
      ? lazySchemas
      : keyBy(schemasQuery.data?.getAllAvroSchemas.schemas, "sourceStreamName")
    : null;

  const selectedStreams = Object.values(sourceStreams).filter(Boolean);

  const allTablesSelected =
    selectableStreams.length > 0 &&
    selectableStreams.every((s) => !!sourceStreams[s.name]);

  const showEnableAllHistoryTablesButton = selectedStreams.length > 0;
  const allHistoryTablesEnabled = selectedStreams.every(
    (value) => value?.enabledHistoryTable,
  );

  if (
    streamsStatus.status === AsyncJobStatusEnum.Processing ||
    streamsStatus.status === AsyncJobStatusEnum.Cancelled
  ) {
    return (
      <>
        <div className="flex flex-col items-center justify-center gap-5">
          <LoadingSpinner className="size-5" />
          {usesAsyncStreams &&
          streamsStatus.status === AsyncJobStatusEnum.Processing &&
          streamsStatus.startedAt ? (
            <>
              <div className="text-sm dark:text-gray-300">
                Loading the tables for this connection might take several
                minutes...
              </div>
              <div className="text-sm dark:text-gray-300">
                Started loading{" "}
                <FormatRelativeTime date={streamsStatus.startedAt} />
              </div>
              <Button
                size="xs"
                colorScheme="secondary"
                variant="outline"
                disabled={loadingCancel}
                onClick={handleCancel}
                icon={<XIcon className="size-4" />}
              >
                <div>Cancel</div>
              </Button>
            </>
          ) : null}
        </div>
      </>
    );
  }

  if (streamsStatus.status === AsyncJobStatusEnum.Error) {
    return (
      <div className="flex items-center justify-center">
        <div className="text-sm dark:text-gray-300">
          Error loading tables from {props.connection?.label ?? "connection"}
        </div>
      </div>
    );
  }

  if (!streams.length) {
    return (
      <p className="text-sm dark:text-gray-300">
        There are no tables found for this integration.
      </p>
    );
  }

  if (sourceIntegration == null) {
    return null;
  }

  return (
    <SourceStreamsStateContext.Provider value={[sourceStreams, dispatch]}>
      <div>
        <div
          className={cn(
            props.mode === "create" &&
              "sticky top-0 z-10 bg-white pt-2 dark:bg-gray-800",
          )}
        >
          {streams.length > 10 && (
            <InputGroup>
              <InputAddonLeft>
                <SearchIcon
                  className={cn(props.size === "small" ? "size-4" : "size-5")}
                />
              </InputAddonLeft>
              <Input
                type="text"
                placeholder="Search tables..."
                className={cn(props.size === "small" && "h-8 text-xs")}
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                }}
              />
            </InputGroup>
          )}
          <div className="mb-4 flex items-center justify-between py-1">
            <div className="text-xs">
              {search
                ? `${allFilteredTablesCount} result${
                    allFilteredTablesCount !== 1 ? "s" : ""
                  } of ${allTablesCount} table${
                    allTablesCount !== 1 ? "s" : ""
                  }`
                : `${allTablesCount} table${streams.length !== 1 ? "s" : ""}`}
            </div>

            {sourceIntegration?.config?.hideSelectAllTables ? null : (
              <div className="flex items-center gap-2">
                {!allTablesSelected ? (
                  <UtilityButton
                    onClick={() => {
                      dispatch({
                        type: "set_source_streams",
                        payload: {
                          streams: streams.filter((s) => s.selectable),
                        },
                      });
                    }}
                  >
                    Select all tables
                  </UtilityButton>
                ) : (
                  <UtilityButton
                    onClick={() => {
                      dispatch({
                        type: "set_source_streams",
                        payload: { streams: [] },
                      });
                    }}
                  >
                    Deselect all tables
                  </UtilityButton>
                )}
                {showEnableAllHistoryTablesButton &&
                  (allHistoryTablesEnabled ? (
                    <UtilityButton
                      onClick={() => {
                        dispatch({
                          type: "set_history_table_all",
                          payload: { enabled: false },
                        });
                      }}
                    >
                      Disable all history tables
                    </UtilityButton>
                  ) : (
                    <UtilityButton
                      onClick={() => {
                        dispatch({
                          type: "set_history_table_all",
                          payload: { enabled: true },
                        });
                      }}
                    >
                      Enable all history tables
                    </UtilityButton>
                  ))}
              </div>
            )}
          </div>
        </div>
        <div className={cn(props.mode === "edit" && "overflow-auto pl-2")}>
          <ConnectionCDCFeaturesProvider
            connection={props.connection}
            sourceSettings={props.sourceSettings}
          >
            <CdcUnavailableNotice />
            {filteredStreams.map((stream: Stream) => (
              <StreamItem
                key={stream.name}
                stream={stream}
                search={search}
                sourceIntegration={sourceIntegration}
                schema={mappedSchemas?.[stream.name]}
                required={props.requiredStreams?.includes(stream.name)}
                syncInterval={
                  props.streamSyncIntervals?.[stream.name] ?? props.syncInterval
                }
              />
            ))}
          </ConnectionCDCFeaturesProvider>
        </div>
      </div>
    </SourceStreamsStateContext.Provider>
  );
}
